// Affiliate Program Plan Task 7 — FirstPromoter v2 tracking init for the
// frontend/ app. Same contract as web/app/lib/firstpromoter.ts:
//   - SSR-safe (no-op when window is undefined; frontend/ is an SPA so this is
//     belt-and-suspenders)
//   - Idempotent
//   - Local-dev safe (no-op when the cid is empty)
//
// CDN file is `fpr.js` (v2), NOT the legacy v1 `fprom.js`. The queue shim must
// run before the script is appended so fpr.js can drain window.fpr.q on load.
// Cookie domain spans web/ + frontend/ via FP's Domain=.hookmyapp.com behavior
// (matches the PostHog cross_subdomain_cookie pattern in web/app/lib/analytics.ts).

type FprQueue = { (...args: unknown[]): void; q?: unknown[][] };

// Augment Window with the FirstPromoter v2 fpr queue so source + test files
// can access window.fpr without a double-cast.
declare global {
  interface Window {
    fpr?: FprQueue;
  }
}

let initialized = false;

export function initFirstPromoter(): void {
  if (typeof window === 'undefined' || initialized) return;
  const cid = import.meta.env.VITE_FIRSTPROMOTER_CID;
  if (!cid) return;

  if (!window.fpr) {
    const fpr: FprQueue = (...args: unknown[]) => {
      fpr.q = fpr.q || [];
      if (args[0] === 'set') fpr.q.unshift(args);
      else fpr.q.push(args);
    };
    window.fpr = fpr;
  }
  window.fpr('init', { cid });
  window.fpr('click');

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://cdn.firstpromoter.com/fpr.js';
  document.head.appendChild(script);

  initialized = true;
}

// Converts the cookie'd visitor into a FirstPromoter "referral" (lead) by
// calling fpr("referral", ...) — the v2 replacement for the legacy v1
// $FPROM.trackSignup(). Safe to call before fpr.js finishes loading: the
// queue shim buffers the call and fpr.js replays it on load.
export function trackSignup(args: { email: string; uid: string }): void {
  if (typeof window === 'undefined') return;
  if (!window.fpr) return;
  window.fpr('referral', args);
}
